/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { Condition } from "@/types/search";

import { InvocationStatus } from "./ActionRecord";

/** The invocations whose input names the session by ID, as the invocation search API matches them. */
export const invocationSessionCondition = (sessionId: string): Condition => ({
  field: "input.session_id",
  comparator: "EQUALS",
  value: sessionId,
});

export const invocationDatasetCondition = (datasetId: string): Condition => {
  return {
    conditions: [
      {
        field: "data_source_id" as const,
        value: datasetId,
        comparator: "EQUALS" as const,
      },
      {
        field: "data_source_type" as const,
        value: "Dataset",
        comparator: "EQUALS" as const,
      },
    ],
    operator: "AND" as const,
  };
};

export const invocationInitiatorCondition = (
  initiatorId: string,
  initiatorType: "Manual" | "Trigger",
): Condition => {
  return {
    conditions: [
      {
        field: "provenance.source.source_id" as const,
        value: initiatorId,
        comparator: "EQUALS" as const,
      },
      {
        field: "provenance.source.source_type" as const,
        value: initiatorType,
        comparator: "EQUALS" as const,
      },
    ],
    operator: "AND" as const,
  };
};

export const invocationActionCondition = (
  actionOrgId: string,
  actionName: string,
): Condition => {
  // Add action digest as a condition when support for viewing details
  // of an action at a particular version is added.
  return {
    conditions: [
      {
        field: "provenance.action.org_id" as const,
        value: actionOrgId,
        comparator: "EQUALS" as const,
      },
      {
        field: "provenance.action.name" as const,
        value: actionName,
        comparator: "EQUALS" as const,
      },
    ],
    operator: "AND" as const,
  };
};

export const invocationStatusCondition = (
  statuses: InvocationStatus[],
): Condition => {
  /**
   * Creates a search condition for filtering invocations by their status.
   *
   * @param statuses - Array of InvocationStatus values to filter by
   * @returns A Condition that matches invocations with any of the provided statuses
   * @throws Error if the statuses array is empty
   */
  if (statuses.length === 0) {
    throw new Error("At least one status must be provided");
  }

  if (statuses.length === 1) {
    return {
      field: "last_status" as const,
      value: statuses[0],
      comparator: "EQUALS" as const,
    };
  }

  return {
    conditions: statuses.map((status) => ({
      field: "last_status" as const,
      value: status,
      comparator: "EQUALS" as const,
    })),
    operator: "OR" as const,
  };
};

export const invocationDateRangeCondition = (
  startTime: string, // UTC ISO8601 string
  endTime: string, // UTC ISO8601 string
): Condition => {
  return {
    conditions: [
      {
        field: "created" as const,
        value: startTime,
        comparator: "GREATER_THAN_OR_EQUAL" as const,
      },
      {
        field: "created" as const,
        value: endTime,
        comparator: "LESS_THAN_OR_EQUAL" as const,
      },
    ],
    operator: "AND" as const,
  };
};

export const invocationSourceCondition = (
  sourceType: "Manual" | "Trigger",
): Condition => {
  return {
    field: "provenance.source.source_type" as const,
    value: sourceType,
    comparator: "EQUALS" as const,
  };
};
